import React, { useRef, useEffect } from 'react';
import { Box, TextField } from '@mui/material';

const CustomOtpInput = ({ 
  value, 
  onChange, 
  numInputs = 6, 
  inputStyle = {}, 
  focusStyle = {},
  shouldAutoFocus = true 
}) => {
  const inputRefs = useRef([]);

  useEffect(() => {
    if (shouldAutoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [shouldAutoFocus]);

  const handleChange = (index, inputValue) => {
    if (inputValue.length > 1) {
      inputValue = inputValue.slice(-1); // Take only the last character
    }

    const newValue = value.split('');
    newValue[index] = inputValue;
    const newOtp = newValue.join('');

    onChange(newOtp);

    // Move to next input if current input is filled
    if (inputValue && index < numInputs - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Move to previous input on backspace if current input is empty
    if (e.key === 'Backspace' && !value[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').slice(0, numInputs);
    if (/^\d+$/.test(pastedData)) {
      onChange(pastedData.padEnd(numInputs, ''));
      // Focus the next empty input or the last input
      const nextIndex = Math.min(pastedData.length, numInputs - 1);
      inputRefs.current[nextIndex]?.focus();
    }
  };

  const baseInputStyle = {
    width: '2.8rem',
    height: '3.2rem',
    margin: '0 0.3rem',
    fontSize: '1.4rem',
    textAlign: 'center',
    borderRadius: '12px',
    border: '2px solid rgba(25, 118, 210, 0.2)',
    backgroundColor: '#fff',
    transition: 'all 0.2s ease',
    ...inputStyle
  };

  const baseFocusStyle = {
    borderColor: '#1976d2',
    boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)',
    outline: 'none',
    transform: 'scale(1.05)',
    ...focusStyle
  };

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        gap: 0.5
      }}
      onPaste={handlePaste}
    >
      {Array.from({ length: numInputs }, (_, index) => (
        <TextField
          key={index}
          inputRef={(el) => (inputRefs.current[index] = el)}
          value={value[index] || ''}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          inputProps={{
            maxLength: 1,
            style: {
              ...baseInputStyle,
              ...(document.activeElement === inputRefs.current[index] ? baseFocusStyle : {})
            }
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                border: 'none',
              },
              '&:hover fieldset': {
                border: 'none',
              },
              '&.Mui-focused fieldset': {
                border: 'none',
              },
            },
            '& .MuiInputBase-input': {
              textAlign: 'center',
              padding: '8px',
            }
          }}
        />
      ))}
    </Box>
  );
};

export default CustomOtpInput;
